import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// MediaAttachment / MediaAlbum — рендер вложений в пузыре (FR-MEDIA-01..04, 10).
//   image  → миниатюра (thumbnailUrl), клик → просмотрщик
//   video  → постер + кнопка play (инлайн)
//   audio  → файловый плеер-плашка с длительностью
//   file   → плашка: иконка по kind, filename, человекочитаемый size, скачать
// Несколько image → MediaAlbum (сетка), на последней «+K» при переполнении.
// Прогресс-кольцо поверх миниатюры при uploadProgress (FR-MEDIA-07). Презентационный.
// DLP-5 (из форка «Наш.Чат»): плитка — спиннер, пока грузится миниатюра (сбой → иконка); кольцо
// прогресса скачивания «как файл» поверх плитки; плашка файла — прогресс скачивания с отменой и фаза
// «Готово — нажмите, чтобы сохранить» (iOS: сохранение требует нового касания).
import { useState } from 'react';
import { Icon } from '../primitives/Icon.js';
import { Spinner } from '../primitives/Spinner.js';
import { ProgressBar, ProgressRing, progressPercent } from '../primitives/Progress.js';
import { formatFileSize, formatDuration, formatTransferProgress } from '../util/format.js';
/** Сколько плиток показываем в альбоме до «+K». */
const ALBUM_MAX_TILES = 4;
function iconForFile(att) {
    if (att.kind === 'image')
        return 'image';
    if (att.kind === 'video')
        return 'video';
    return 'file';
}
function ProgressOverlay({ progress }) {
    const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);
    const overlay = {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '4px',
        background: 'var(--color-overlay)',
        color: 'var(--color-text-inverse)',
        borderRadius: 'inherit',
        fontSize: 'var(--font-xs)',
    };
    return (_jsxs("span", { "data-upload-progress": "", "data-progress": pct, style: overlay, children: [_jsx(Spinner, { size: 22 }), _jsxs("span", { children: [pct, "%"] })] }));
}
/** Кольцо скачивания в углу плитки (картинку/видео качают кнопкой «Скачать» просмотрщика). */
function TileDownloadBadge({ transfer }) {
    return (_jsxs("span", { "data-tile-download": "", style: {
            position: 'absolute',
            right: '6px',
            bottom: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 6px 2px 2px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-overlay)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-xs)',
            pointerEvents: 'none',
        }, children: [_jsx(ProgressRing, { value: transfer.progress, size: 22, thickness: 2.5, color: "var(--color-text-inverse)", trackColor: "rgba(255,255,255,0.3)", label: "\u0421\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435" }), transfer.progress !== undefined ? `${progressPercent(transfer.progress)}%` : null] }));
}
/** Одиночная плитка изображения/видео. */
function MediaTile({ att, index, onOpen, uploadProgress, transfer, extra, }) {
    // Миниатюра грузится по сети — до onLoad показываем спиннер (серая плитка выглядела «зависшей»);
    // сбой (404 — превью ещё генерится, сеть) → иконка по kind.
    const [thumb, setThumb] = useState('loading');
    const tile = {
        position: 'relative',
        display: 'block',
        width: '100%',
        aspectRatio: '1 / 1',
        border: 'none',
        padding: 0,
        overflow: 'hidden',
        borderRadius: 'var(--radius-control)',
        background: 'var(--color-surface-hover)',
        cursor: 'pointer',
    };
    const img = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
    const center = {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-muted)',
    };
    const showThumb = att.thumbnailUrl !== undefined && thumb !== 'failed';
    return (_jsxs("button", { type: "button", "data-media-tile": att.kind, "data-thumb-state": showThumb ? thumb : 'none', style: tile, onClick: () => onOpen(index), children: [showThumb ? (_jsx("img", { src: att.thumbnailUrl, alt: att.filename, style: { ...img, opacity: thumb === 'loaded' ? 1 : 0, transition: 'opacity 160ms ease-out' }, onLoad: () => setThumb('loaded'), onError: () => setThumb('failed') })) : (_jsx("span", { style: center, children: _jsx(Icon, { name: iconForFile(att), size: 28 }) })), showThumb && thumb === 'loading' ? (_jsx("span", { "data-thumb-loading": "", style: center, children: _jsx(Spinner, { size: 24, label: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043F\u0440\u0435\u0432\u044C\u044E" }) })) : null, att.kind === 'video' ? (_jsx("span", { "data-media-play": "", style: {
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                }, children: _jsx(Icon, { name: "play", size: 36 }) })) : null, extra && extra > 0 ? (_jsxs("span", { "data-media-extra": extra, style: {
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--color-overlay)',
                    color: 'var(--color-text-inverse)',
                    fontSize: 'var(--font-xl)',
                    fontWeight: 'var(--weight-semibold)',
                }, children: ["+", extra] })) : null, transfer !== undefined && transfer.phase === 'downloading' ? _jsx(TileDownloadBadge, { transfer: transfer }) : null, uploadProgress !== undefined ? _jsx(ProgressOverlay, { progress: uploadProgress }) : null] }));
}
/** Плашка файла/аудио (FR-MEDIA-03/04) + прогресс скачивания (DLP-5). */
function FileRow({ att, index, onOpen, onDownload, transfer, onCancelTransfer, }) {
    const row = {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2)',
        borderRadius: 'var(--radius-control)',
        background: 'var(--color-surface-hover)',
        // не шире родителя: иначе nowrap-имя файла задаёт min-content и ellipsis никогда не наступает
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
    };
    const meta = { fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' };
    const isAudio = att.kind === 'audio';
    const downloading = transfer?.phase === 'downloading';
    const ready = transfer?.phase === 'ready';
    const roundBtn = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        background: downloading ? 'transparent' : 'var(--color-accent)',
        color: downloading ? 'var(--color-accent)' : 'var(--color-text-inverse)',
        cursor: 'pointer',
        flex: '0 0 auto',
    };
    const metaText = downloading
        ? transfer.loadedBytes !== undefined
            ? `Скачивание ${formatTransferProgress(transfer.loadedBytes, transfer.totalBytes ?? att.size)}`
            : 'Скачивание…'
        : ready
            ? 'Готово — нажмите, чтобы сохранить'
            : isAudio && att.durationMs
                ? formatDuration(att.durationMs)
                : formatFileSize(att.size);
    return (_jsxs("div", { "data-file-row": att.kind, "data-transfer": transfer?.phase, style: row, children: [downloading ? (_jsx("button", { type: "button", "data-cancel-transfer": "", "aria-label": "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u0441\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435", onClick: () => onCancelTransfer?.(index), style: roundBtn, children: _jsx(ProgressRing, { value: transfer.progress, size: 40, thickness: 3, label: "\u0421\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435", children: _jsx(Icon, { name: "close", size: 16 }) }) })) : (_jsx("button", { type: "button", "aria-label": ready ? 'Сохранить' : isAudio ? 'Воспроизвести' : 'Открыть', "data-save-transfer": ready ? '' : undefined, onClick: () => (ready ? (onDownload ?? onOpen)(index) : onOpen(index)), style: roundBtn, children: _jsx(Icon, { name: ready ? 'download' : isAudio ? 'play' : iconForFile(att), size: 20 }) })), _jsxs("span", { style: { minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }, children: [_jsx("span", { style: {
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: 'var(--font-sm)',
                        }, children: att.filename }), downloading ? _jsx(ProgressBar, { value: transfer.progress, height: 3, label: "\u0421\u043A\u0430\u0447\u0438\u0432\u0430\u043D\u0438\u0435" }) : null, _jsx("span", { "data-file-meta": "", style: { ...meta, ...(ready ? { color: 'var(--color-accent)' } : {}) }, children: metaText })] }), downloading || ready ? null : (_jsx("button", { type: "button", "aria-label": "\u0421\u043A\u0430\u0447\u0430\u0442\u044C", onClick: () => (onDownload ?? onOpen)(index), style: {
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                }, children: _jsx(Icon, { name: "download", size: 20 }) }))] }));
}
export function MediaAttachment({ attachments, onOpen, onDownload, uploadProgress, transferOf, onCancelTransfer, }) {
    if (attachments.length === 0)
        return null;
    const visuals = attachments.filter((a) => a.kind === 'image' || a.kind === 'video');
    const files = attachments.filter((a) => a.kind === 'audio' || a.kind === 'file');
    // Несколько визуальных вложений → альбом-сетка (FR-MEDIA-10).
    const isAlbum = visuals.length >= 2;
    return (_jsxs("div", { "data-media": "", style: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0, maxWidth: '100%' }, children: [isAlbum ? (_jsx(MediaAlbum, { attachments: attachments, onOpen: onOpen, uploadProgress: uploadProgress, transferOf: transferOf })) : (visuals.map((att) => {
                const index = attachments.indexOf(att);
                return (_jsx("div", { style: { maxWidth: '260px' }, "data-media-single": "", children: _jsx(MediaTile, { att: att, index: index, onOpen: onOpen, uploadProgress: uploadProgress, transfer: transferOf?.(att.id) }) }, att.id));
            })), files.map((att) => {
                const index = attachments.indexOf(att);
                return (_jsx(FileRow, { att: att, index: index, onOpen: onOpen, onDownload: onDownload, transfer: transferOf?.(att.id), onCancelTransfer: onCancelTransfer }, att.id));
            })] }));
}
/** Сетка картинок/видео одним сообщением; на последней плитке «+K» при переполнении (FR-MEDIA-10). */
export function MediaAlbum({ attachments, onOpen, uploadProgress, transferOf }) {
    const visuals = attachments.filter((a) => a.kind === 'image' || a.kind === 'video');
    const shown = visuals.slice(0, ALBUM_MAX_TILES);
    const overflow = visuals.length - shown.length;
    const cols = shown.length <= 1 ? 1 : 2;
    const grid = {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 'var(--space-1)',
        maxWidth: '300px',
    };
    return (_jsx("div", { "data-album": "", "data-count": visuals.length, style: grid, children: shown.map((att, i) => {
            const index = attachments.indexOf(att);
            const isLast = i === shown.length - 1;
            return (_jsx(MediaTile, { att: att, index: index, onOpen: onOpen, uploadProgress: uploadProgress, transfer: transferOf?.(att.id), extra: isLast && overflow > 0 ? overflow : undefined }, att.id));
        }) }));
}
//# sourceMappingURL=MediaAttachment.js.map